import { FormControl, TextField, InputLabel, MenuItem, Select, FormControlLabel, Checkbox } from '@material-ui/core'
import { Alert, Box, Button, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from '@mui/x-data-grid'
import moment from 'moment'
import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'


export const UserManagement = ({ user: loggedInUser, users, setUsers, fetchAllUsers, setAlert }) => {
    const [selectedUserId, setSelectedUserId] = useState()

    useEffect(() => {
        if (!selectedUserId)
            fetchAllUsers()
    }, [loggedInUser, selectedUserId])

    const onEdit = userId => setSelectedUserId(userId)
    const onClickAddUser = () => setSelectedUserId(-1)
    const navigateToUsersTable = () => {
        setUsers([])
        setSelectedUserId(null)
    }

    const getUser = userId => users?.find(u => u?.id === userId)
    const getOtherUsersExcept = userId => users?.filter(user => user?.id !== userId)

    const Layout = ({ children }) => {
        return <Paper component={Grid} container marginTop={5} padding={2}
            justifyContent={'center'} alignContent={'center'} alignItems={'center'}>
            {children}
        </Paper>
    }


    const columns = [
        { field: 'id', headerName: 'ID', width: 100 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'email', headerName: 'Email', width: 250 },
        { field: 'mobile', headerName: 'Mobile', width: 130 },
        {
            field: 'active', headerName: 'Is Active', width: 80, type: 'boolean',
        },
        { field: 'referredBy', headerName: 'Referred By', width: 100 },
        { field: 'feesPercent', headerName: 'Fees Percent', width: 100 },
        { field: 'referralCommissionPercent', headerName: 'Referral Commission Percent', width: 100 },
        {
            field: 'role', headerName: 'Role', width: 120,
        },
        {
            field: 'createdOn', headerName: 'Created On', width: 150,
            renderCell: params => (
                <Tooltip
                    title={params?.value}
                >
                    <span>{moment(params?.value)?.fromNow()}</span>
                </Tooltip>
            ),
        },
        {
            field: 'action', headerName: 'Action', width: 150,
            renderCell: params => (
                <Button
                    variant="contained"
                    onClick={() => onEdit(params?.row?.id)}
                >
                    EDIT
                </Button>
            ),
        },
    ];

    if (!selectedUserId)
        return (
            <Layout>
                <Grid container justifyContent='space-between'>
                    <Grid item>
                        <Typography variant="h4" gutterBottom component="div">
                            Manage users
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button variant='contained' onClick={onClickAddUser}>ADD USER</Button>
                    </Grid>
                </Grid >
                <Grid container>
                    <DataGrid
                        slots={{
                            toolbar: () => <GridToolbarContainer style={{ justifyContent: 'space-around' }}>
                                <GridToolbarColumnsButton />
                                <GridToolbarFilterButton />
                                <GridToolbarDensitySelector />
                                <GridToolbarExport />
                                <GridToolbarQuickFilter />
                            </GridToolbarContainer>
                        }}
                        columns={columns}
                        rows={users || []}
                        pageSizeOptions={[]}
                        autoHeight={true}
                    />
                </Grid>
            </Layout>
        )

    if (selectedUserId === -1) {
        return <>
            <Layout >
                <Grid container justifyContent='space-between'>
                    <Grid item>
                        <Typography variant="h4" gutterBottom component="div">
                            Add a User
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button variant='contained' color='error' onClick={navigateToUsersTable}>Cancel</Button>
                    </Grid>
                </Grid >
                <Grid container>
                    <UserForm users={users} loggedInUser={loggedInUser}
                        navigateToUsersTable={navigateToUsersTable} setAlert={setAlert} />
                </Grid>
            </Layout>
        </>
    }

    else {
        return <>
            <Layout>
                <Grid container justifyContent='space-between'>
                    <Grid item>
                        <Typography variant="h4" gutterBottom component="div">
                            Edit a User
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Button variant='contained' color='error' onClick={navigateToUsersTable}>Cancel</Button>
                    </Grid>
                    <Grid container>
                        <UserForm users={getOtherUsersExcept(selectedUserId)} selectedUser={getUser(selectedUserId)}
                            loggedInUser={loggedInUser} navigateToUsersTable={navigateToUsersTable} setAlert={setAlert} />
                    </Grid>
                </Grid >
            </Layout>
        </>
    }

}


const UserForm = ({ users, selectedUser, loggedInUser, navigateToUsersTable, setAlert }) => {
    const { register, reset, control, handleSubmit, formState: { errors } } = useForm()

    useEffect(() => {
        if (selectedUser)
            reset(selectedUser)
    }, [selectedUser])

    const onsubmit = data => {
        fetch('/algo-trading/api/admin/users', {
            method: selectedUser ? 'PUT' : 'POST',
            headers: {
                "Content-Type": "application/json",
                'Authorization': loggedInUser?.credential,
            },
            body: JSON.stringify(data)
        }).then(r => r.text()).then(r => {
            navigateToUsersTable()
            setAlert({
                message: 'User saved successfully',
                open: true,
                severity: 'success'
            })
        }).catch(error => {
            console.error('Error saving user:', error);
            setAlert({
                message: 'Error saving user: ' + error.message,
                open: true,
                severity: 'error'
            });
        })
    }
    return <>
        <Box component='form' onSubmit={handleSubmit(onsubmit)}>
            <Grid container spacing={2} >
                {selectedUser && <input type='hidden' {...register('id')} />}
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <TextField {...register('name')} label='Full Name' variant='outlined' required fullWidth />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <TextField {...register('email')} type='email' label='Email' variant='outlined' required fullWidth />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <TextField {...register('mobile')} label='Mobile' variant='outlined' required fullWidth />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12} >
                    <FormControl fullWidth>
                        <InputLabel id="referred-by" style={{ paddingLeft: 10 }}>Referred by</InputLabel>
                        <Controller
                            name={`referredBy`}
                            control={control}
                            labelId='referred-by'
                            defaultValue={''}
                            rules={{
                                validate: {
                                    isString: v => {
                                        if (!v) return true
                                        return typeof v === 'string' || v instanceof String
                                    },
                                }
                            }}
                            render={({ field }) => (
                                <Select {...field} variant='outlined' fullWidth>
                                    <MenuItem value={''}>Select</MenuItem>
                                    {users?.map(u => <MenuItem value={u?.id} key={u?.id}>{u?.name}</MenuItem>)}
                                </Select>
                            )}
                        />
                    </FormControl>
                    {errors?.referredBy && <Alert severity='warning'>Please select 'Referred By'</Alert>}
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <TextField {...register('feesPercent')} label='Fees Percent' variant='outlined' type='number'
                        InputProps={{ inputProps: { min: 0, max: 100, step: '.01' }, endAdornment: '%' }}

                        fullWidth />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <TextField {...register('referralCommissionPercent')}
                        label='Referral Commission Percent' variant='outlined' type='number'
                        InputProps={{ inputProps: { min: 0, max: 100, step: '.01' }, endAdornment: '%' }}
                        fullWidth />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControlLabel
                        control={
                            <Controller
                                name='active'
                                control={control}
                                defaultValue={true}
                                render={({ field: props }) => (
                                    <Checkbox
                                        {...props}
                                        checked={props.value}
                                        onChange={(e) => props.onChange(e.target.checked)}
                                    />
                                )}
                            />
                        }
                        label='Is Active'
                    />
                </Grid>
                <Grid item lg={3} md={4} sm={6} xs={12} >
                    <FormControl fullWidth>
                        <InputLabel id="role" style={{ paddingLeft: 10 }}>Role</InputLabel>
                        <Controller
                            name={`role`}
                            control={control}
                            labelId='role'
                            defaultValue={'CUSTOMER'}
                            rules={{
                                validate: {
                                    isString: v => typeof v === 'string' || v instanceof String
                                }
                            }}
                            render={({ field }) => (
                                <Select {...field} variant='outlined' fullWidth>
                                    <MenuItem value={'CUSTOMER'}>CUSTOMER</MenuItem>
                                    <MenuItem value={'ADMIN'}>ADMIN</MenuItem>
                                    <MenuItem value={'FNO_ADMIN'}>FNO_ADMIN</MenuItem>
                                </Select>
                            )}
                        />
                    </FormControl>
                    {errors?.role && <Alert severity='warning'>Please select 'Role'</Alert>}
                </Grid>
                <Grid container margin={2} justifyContent='center'>
                    <Button variant='contained' type='submit'>Save</Button>
                </Grid>
            </Grid>
        </Box>
    </>
}